import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/shared/Modal';
import { toast } from '@/lib/swal';
import { useHasPermission } from '@/hooks/use-permissions';
import {
  manufacturerService,
  type Manufacturer,
  type CreateManufacturerPayload,
} from '@/services/manufacturer.service';
import {
  equipmentCategoryService,
  type EquipmentCategory,
  type CreateEquipmentCategoryPayload,
} from '@/services/equipment-category.service';
import {
  equipmentTypeService,
  type EquipmentType,
  type CreateEquipmentTypePayload,
} from '@/services/equipment-type.service';
import { type EquipmentModel } from '@/services/equipment-model.service';
import { ManufacturerForm } from '../ManufacturersPage';
import { CategoryForm } from '../EquipmentCategoriesPage';
import { EquipmentTypeForm } from '../EquipmentTypesPage';
import { EquipmentModelForm, type FormSubmitData } from '../EquipmentModelsPage';
import { saveEquipmentModel } from '../equipment-model-save';

/**
 * Inline master-data creation for the intake workflow (Phase C).
 *
 * Each modal is a thin shell — dialog + the existing Master Data form + the
 * create mutation — so Operations can add a missing manufacturer, category,
 * equipment type or model without leaving the Equipment Intake Workspace. No
 * form markup or validation is reimplemented here; the four forms are imported
 * from their Master Data pages.
 *
 * Mirrors `features/equipment-intake/CustomerInlineModals.tsx`.
 *
 * Every modal renders at the caller's top level (never inside another modal's
 * children), so the shared `Modal` stack owns Escape targeting, the refcounted
 * scroll lock and z-index ordering.
 */

function apiMessage(error: unknown, fallback: string): string {
  const maybe = error as { response?: { data?: { error?: { message?: string } } } } | undefined;
  return maybe?.response?.data?.error?.message ?? fallback;
}

// ── Manufacturer (C.2) ───────────────────────────────────────────────────────

export function AddManufacturerModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (manufacturer: Manufacturer) => void;
}) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: CreateManufacturerPayload) => manufacturerService.create(payload),
    onSuccess: (manufacturer) => {
      void qc.invalidateQueries({ queryKey: ['manufacturers-all'] });
      toast.success('Manufacturer created');
      onCreated(manufacturer);
      onClose();
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not create manufacturer')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add Manufacturer">
      <ManufacturerForm
        onSubmit={(payload) => mutation.mutate(payload)}
        onCancel={onClose}
        saving={mutation.isPending}
      />
    </Modal>
  );
}

// ── Category (C.3) ───────────────────────────────────────────────────────────

export function AddCategoryModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (category: EquipmentCategory) => void;
}) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: CreateEquipmentCategoryPayload) =>
      equipmentCategoryService.create(payload),
    onSuccess: (category) => {
      void qc.invalidateQueries({ queryKey: ['equipment-categories-all'] });
      toast.success('Category created');
      onCreated(category);
      onClose();
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not create category')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add Category">
      <CategoryForm
        onSubmit={(payload) => mutation.mutate(payload)}
        onCancel={onClose}
        saving={mutation.isPending}
      />
    </Modal>
  );
}

// ── Equipment Type, with nested Category creation (C.3) ──────────────────────

export function AddEquipmentTypeModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (equipmentType: EquipmentType) => void;
}) {
  const qc = useQueryClient();
  const can = useHasPermission();
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  // Set after a category is created inline; the type form selects it without
  // losing the name/description the operator has already typed.
  const [createdCategoryId, setCreatedCategoryId] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: (payload: CreateEquipmentTypePayload) => equipmentTypeService.create(payload),
    onSuccess: (equipmentType) => {
      void qc.invalidateQueries({ queryKey: ['equipment-types-all'] });
      toast.success('Equipment type created');
      onCreated(equipmentType);
      onClose();
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not create equipment type')),
  });

  return (
    <>
      <Modal open={open} onClose={onClose} title="Add Equipment Type">
        <EquipmentTypeForm
          onSubmit={(payload) => mutation.mutate(payload)}
          onCancel={onClose}
          saving={mutation.isPending}
          onAddCategory={can('category:create') ? () => setCategoryModalOpen(true) : undefined}
          selectCategoryId={createdCategoryId}
        />
      </Modal>

      {/* Sibling, not a child of the dialog above — see the module comment. */}
      <AddCategoryModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onCreated={(category) => setCreatedCategoryId(category.id)}
      />
    </>
  );
}

// ── Equipment Model (C.4) ────────────────────────────────────────────────────

export function AddEquipmentModelModal({
  open,
  onClose,
  manufacturerId,
  equipmentTypeId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Pre-selected from the equipment form; not re-selectable (C.4). */
  manufacturerId: string;
  equipmentTypeId: string;
  onCreated: (model: EquipmentModel) => void;
}) {
  const qc = useQueryClient();

  const mutation = useMutation({
    // Reuses the shared helper so the create + image-upload sequence exists once.
    mutationFn: (data: FormSubmitData) =>
      saveEquipmentModel({
        payload: data.payload,
        pendingFiles: data.pendingFiles,
        removedImageIndices: data.removedImageIndices,
      }),
    onSuccess: (model) => {
      void qc.invalidateQueries({
        queryKey: ['equipment-models-all', manufacturerId, equipmentTypeId],
      });
      toast.success('Model created');
      onCreated(model);
      onClose();
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not create model')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add Equipment Model" size="lg">
      <EquipmentModelForm
        initial={{ manufacturerId, equipmentTypeId }}
        lockRelations
        onSubmit={(data) => mutation.mutate(data)}
        onCancel={onClose}
        saving={mutation.isPending}
      />
    </Modal>
  );
}
